import { useMemo } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import get from "lodash/get";
import { toast } from "sonner";

import { queryClient } from "App";
import { formatError } from "helpers/formatError";
import { addResizedColumns } from "./useTableConfig";
import { API_KEYS_TABLE_CONSTANTS } from "routes/admin/components/api_keys_table/ApiKeysTableV3";
import ApiKeyService from "services/ApiKey";
import UserService from "services/User";
import {
  getApiKeysTableColumns,
  getApiKeysTableFilters,
} from "routes/admin/components/api_keys_table/ApiKeysTableColumnsAndFilters";

export const invalidateApiKeysQuery = async () => {
  await queryClient.invalidateQueries({
    queryKey: [API_KEYS_TABLE_CONSTANTS.queryKey],
  });
};

// `create` has no error toast: BaseForm already shows a failed submit in the
// form's error banner.
export const useApiKeyMutations = ({ onRotated, onRevoked } = {}) => {
  const create = useMutation({
    mutationFn: data => ApiKeyService.create(data),
    onSuccess: () => {
      invalidateApiKeysQuery();
      toast.success("API key created");
    },
  });

  const rotate = useMutation({
    mutationFn: id => ApiKeyService.rotate(id),
    onSuccess: response => {
      invalidateApiKeysQuery();
      toast.success("API key rotated");
      onRotated?.(response);
    },
    onError: err => toast.error(formatError(err, "Failed to rotate API key")),
  });

  const revoke = useMutation({
    mutationFn: id => ApiKeyService.revoke(id),
    onSuccess: () => {
      invalidateApiKeysQuery();
      toast.success("API key revoked");
      onRevoked?.();
    },
    onError: err => toast.error(formatError(err, "Failed to revoke API key")),
  });

  return { create, rotate, revoke };
};

export const useApiKeysTable = ({ onRotate, onRevoke }) => {
  const { data: tableSettings, isSuccess: isTableSettingsFetched } = useQuery({
    queryKey: ["table-settings"],
    queryFn: UserService.getUserSettings,
  });

  const columns = useMemo(() => {
    if (!isTableSettingsFetched) return [];
    const initialColumns = getApiKeysTableColumns({ onRotate, onRevoke });
    const apiKeysTableSettings = get(
      tableSettings,
      `user_settings.preferences.tables.${API_KEYS_TABLE_CONSTANTS.tableName}`,
      {}
    );
    return addResizedColumns({
      columns: initialColumns,
      resizedColumns: apiKeysTableSettings?.resized_columns || {},
    });
  }, [tableSettings, isTableSettingsFetched, onRotate, onRevoke]);

  const filters = useMemo(() => getApiKeysTableFilters(), []);

  return { columns, filters };
};
